/**
 * THE 0DTE LOAD PATH, EXTRACTED SO ITS **REFUSAL** CAN BE DRIVEN BY AN ARM.
 *
 * ⛔ WHY THIS IS A MODULE AND NOT AN INLINE `try/catch` IN THE PANEL. GK-nn mutated the panel's
 * catch to `levels0dte = {ok:true, call_wall:8800, ...}` — **fabricating the exact pooled lie this
 * build exists to kill — and ALL 22 SUITES STAYED GREEN.** My own arm pinned the TEXT of the
 * `$derived` expression, so it could not see a fabrication one block away.
 * ⇒ ⭐⭐ ***AN ARM THAT PINS THE SHAPE OF ONE EXPRESSION DEFENDS THAT EXPRESSION, NOT THE PROPERTY.
 * THE MUTATION SITE YOU DID NOT IMAGINE IS THE ONE THAT SHIPS.*** The refusal was implemented and
 * unguarded, and *the one behaviour the object is named for was the one behaviour no arm defended*.
 *
 * So the decision lives HERE, in a function with no DOM and no framework, and the arms drive it
 * with a throwing fetcher. Both the panel and the arms call ONE construction.
 */
import { refusedRowsFault } from "./refusedRows.ts";
import type { DealerLevelsResponse } from "./api";
import { doorFailureEvidence, type DoorFaultEvidence } from "./faultEvidence.ts";

export interface ZeroDteState {
  /** NEVER a fabricated payload. `null` means "do not draw", and the panel says why. */
  levels: DealerLevelsResponse | null;
  /** The server's or the transport's own words. `null` only when `levels` is usable. */
  error: string | null;
  /** Door-qualified evidence. Consumers own the one console edge and the visible surface. */
  fault: DoorFaultEvidence | null;
}

/**
 * ⛔ THE CONTRACT IS A REFUSAL: on ANY failure this returns `levels: null`.
 *
 * There is deliberately no fallback to `/api/dealer/gex`. The pooled ladder is a DIFFERENT
 * QUANTITY (all-expiry, expiry-blind) wearing identical labels — on 2026-08-21 it offers a gamma
 * flip at 8800 where today's 0DTE chain has none at all. Substituting it would leave the chart
 * looking correct while drawing the wrong instrument, and **a wrong line prompts no investigation**.
 */
export async function loadZeroDte(
  fetcher: () => Promise<DealerLevelsResponse>,
): Promise<ZeroDteState> {
  let res: DealerLevelsResponse;
  try {
    res = await fetcher();
  } catch (e) {
    // A transport failure is a REASON, never a licence to substitute another source.
    const evidence = doorFailureEvidence("0DTE levels", e);
    const suffix = refusedRowsFault(e);
    const fault = { ...evidence, message: evidence.message + suffix, label: evidence.label + suffix };
    return { levels: null, error: fault.message, fault };
  }
  if (!res || res.ok !== true) {
    // `ok:false` carries the server's own reason; render it verbatim rather than paraphrasing.
    const fault = doorFailureEvidence("0DTE levels", {
      refusal: true,
      message: res?.reason ?? "the levels route returned not-ok",
    });
    return { levels: null, error: fault.message, fault };
  }
  return { levels: res, error: null, fault: null };
}
